import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cx } from './cx';

/**
 * A labelled input.
 *
 * The label is required and always rendered — `hideLabel` moves it out of sight,
 * not out of the accessibility tree, so a filter bar can look like a filter bar
 * without leaving a screen reader with four unnamed comboboxes.
 *
 * `error` is a string rather than a boolean because the API hands back
 * `{ field, message }` pairs on a 422 and the message it wrote is better copy
 * than anything a component could invent. ApiError.fieldMap() feeds this
 * directly.
 */

/**
 * Optional props are written `?: T | undefined` throughout the primitives.
 *
 * exactOptionalPropertyTypes is on — which is right for the API types, where an
 * absent key genuinely means absent — but in JSX it would reject
 * `<Field error={errors.email} />` for a value that is merely possibly
 * undefined, and every caller would need a conditional spread. The strictness
 * is worth having where it models the wire and is only friction here.
 */
interface FieldShared {
  label: string;
  hideLabel?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  /** Renders "Optional" beside the label. Required is the unmarked default. */
  optional?: boolean | undefined;
  /** A unit, a character count, a "reveal" toggle. */
  trailing?: ReactNode;
}

export type FieldProps = FieldShared &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-describedby' | 'aria-invalid'>;

export function Field({
  label,
  hideLabel = false,
  hint,
  error,
  optional = false,
  trailing,
  className,
  ...rest
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const described = cx(hint !== undefined && hintId, error !== undefined && errorId);

  return (
    <div className={cx('dc-field', className)}>
      <label className={cx('dc-field__label', hideLabel && 'visually-hidden')} htmlFor={id}>
        {label}
        {optional && <span className="dc-field__optional">Optional</span>}
        {trailing}
      </label>
      <input
        {...rest}
        id={id}
        className={cx('dc-input', error !== undefined && 'dc-input--invalid')}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={described === '' ? undefined : described}
      />
      {hint !== undefined && (
        <span className="dc-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        // Polite, not assertive: a validation message that interrupts mid-word
        // is worse than one that waits for the pause.
        <span className="dc-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export type TextAreaProps = FieldShared &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'aria-describedby' | 'aria-invalid'>;

/** The same field, for the note both interfaces attach to a status change. */
export function TextArea({
  label,
  hideLabel = false,
  hint,
  error,
  optional = false,
  trailing,
  className,
  ...rest
}: TextAreaProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const described = cx(hint !== undefined && hintId, error !== undefined && errorId);

  return (
    <div className={cx('dc-field', className)}>
      <label className={cx('dc-field__label', hideLabel && 'visually-hidden')} htmlFor={id}>
        {label}
        {optional && <span className="dc-field__optional">Optional</span>}
        {trailing}
      </label>
      <textarea
        {...rest}
        id={id}
        className={cx('dc-input', error !== undefined && 'dc-input--invalid')}
        aria-invalid={error !== undefined || undefined}
        aria-describedby={described === '' ? undefined : described}
      />
      {hint !== undefined && (
        <span className="dc-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span className="dc-field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
